import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownOption from "components/Common/DropdownOption";
import { useFormik } from "formik";
import { TOKEN_PROGRAMS } from "helpers/constans";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { addToken } from "store/token/thunk";
import * as Yup from "yup";

export interface Props {
    isOpen?: boolean;
    onClose?: () => void;
    triggerRefresh?: () => void;
}

const TokenForm = ({ isOpen = false, onClose, triggerRefresh }: Props) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const TOKEN_PROGRAMS_LANG = TOKEN_PROGRAMS.map((item) => ({
        label: t(item.label),
        value: item.value
    }));


    const onCloseClick = () => {
        onClose && onClose();
        formik.resetForm();
    };

    const handleSubmit = async (values: any) => {
        try {
            setIsLoading(true);
            const data = {
                tokenProgram: values?.tokenProgram?.value || TOKEN_PROGRAMS[0]?.value,
                mint: values?.mint || '',
                standard: values?.standard || '',
                name: values?.name || '',
                symbol: values?.symbol || '',
                logo: values?.logo || '',
                decimals: values?.decimals,
                metaplex: {
                    metadataUri: values?.metaplex?.metadataUri,
                    masterEdition: Boolean(values?.metaplex?.masterEdition),
                    isMutable: Boolean(values?.metaplex?.isMutable),
                    sellerFeeBasisPoints: values?.metaplex?.sellerFeeBasisPoints,
                    primarySaleHappened: values?.metaplex?.primarySaleHappened
                },
                fullyDilutedValue: values?.fullyDilutedValue,
                totalSupply: values?.totalSupply,
                totalSupplyFormatted: values?.totalSupplyFormatted,
                links: {
                    moralis: values?.links?.moralis
                },
                description: values?.description
            };
            const response: any = await addToken(data);
            setIsLoading(false);
            if (response?.data) {
                toast(t("The process has been completed."), CONFIG_OPTION_TOAST_NORMAL);
                triggerRefresh && triggerRefresh();
            } else {
                toast(`${response || "Error - Please try again!"}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading(false);
            toast(`${error?.message || "Error - Please try again!"}`, CONFIG_OPTION_TOAST_ERROR);
        }
    };

    const validationSchema = Yup.object({
        tokenProgram: Yup.object({
            label: Yup.string().required(t("Required")),
            value: Yup.string().required(t("Required"))
        }).nullable(),
        mint: Yup.string().required(t("Required")),
        standard: Yup.string().required(t("Required")),
        name: Yup.string().required(t("Required")),
        symbol: Yup.string().required(t("Required")),
        logo: Yup.string().required(t("Required")),
        decimals: Yup.string().required(t("Required")),
        metaplex: Yup.object({
            metadataUri: Yup.string().required(t("Required")),
            masterEdition: Yup.boolean().required(t("Required")),
            isMutable: Yup.boolean().required(t("Required")),
            sellerFeeBasisPoints: Yup.string().required(t("Required")),
            primarySaleHappened: Yup.string().required(t("Required"))
        }).nullable(),
        fullyDilutedValue: Yup.string().nullable(),
        totalSupply: Yup.string().required(t("Required")),
        totalSupplyFormatted: Yup.string().required(t("Required")),
        links: Yup.object({
            moralis: Yup.string().nullable()
        }).nullable(),
        description: Yup.string().nullable()
    });

    const formik = useFormik({
        initialValues: {
            tokenProgram: TOKEN_PROGRAMS[0],
            mint: '',
            standard: '',
            name: '',
            symbol: '',
            logo: '',
            decimals: '0',
            metaplex: {
                metadataUri: '',
                masterEdition: false,
                isMutable: false,
                sellerFeeBasisPoints: '0',
                primarySaleHappened: '0'
            },
            fullyDilutedValue: '0',
            totalSupply: '0',
            totalSupplyFormatted: '0',
            links: {
                moralis: ''
            },
            description: ''
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    return (
        <Modal isOpen={isOpen} toggle={onCloseClick} className="modal-dialog-centered">
            <ModalHeader>
                <h5 className="modal-title">{t("Add Token")}</h5>
            </ModalHeader>
            <ModalBody>
                <div className="py-2 relative" style={{ minHeight: "calc(100dvh - 200px)" }}>
                    <form onSubmit={formik.handleSubmit}>
                        <Row>

                            <Col sm={12} md={12}>
                                <div className="mb-4">
                                    <Label className="text-primary fs-13">{t("Program")} <span className="text-danger fs-11">*</span></Label>
                                    <DropdownOption
                                        name="tokenProgram"
                                        dataList={TOKEN_PROGRAMS_LANG}
                                        initialValue={formik?.values?.tokenProgram || TOKEN_PROGRAMS_LANG[0]}
                                        onChangeSelect={(event: any) => {
                                            formik.setFieldValue('tokenProgram', event);
                                        }}
                                        className="dropdown-status-rounded z-hight"
                                        classNamePrefix="name-prefix"
                                    />
                                    {/* {formik.touched.tokenProgram && formik.errors.tokenProgram && (
                                        <div className="text-danger fs-12">{formik.errors.tokenProgram?.value || formik.errors.tokenProgram?.label}</div>
                                    )} */}
                                </div>
                            </Col>
                            <Col sm={12} md={12}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Name")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="name"
                                            value={formik.values.name}
                                            placeholder={t("Name")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.name && formik.errors.name)}
                                        />
                                    </div>
                                    {formik.touched.name && formik.errors.name && (
                                        <div className="text-danger fs-12">{formik.errors.name}</div>
                                    )}
                                </div>
                            </Col>

                            <Col sm={12} md={12}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Mint")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="mint"
                                            value={formik.values.mint}
                                            placeholder={t("Mint")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.mint && formik.errors.mint)}
                                        />
                                    </div>
                                    {formik.touched.mint && formik.errors.mint && (
                                        <div className="text-danger fs-12">{formik.errors.mint}</div>
                                    )}
                                </div>
                            </Col>

                            <Col sm={12} md={12}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Logo URL")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="logo"
                                            value={formik.values.logo}
                                            placeholder={t("Logo URL")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.logo && formik.errors.logo)}
                                        />
                                    </div>
                                    {formik.touched.logo && formik.errors.logo && (
                                        <div className="text-danger fs-12">{formik.errors.logo}</div>
                                    )}
                                </div>
                            </Col>

                            <Col sm={12} md={12}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Metadata URI")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="metaplex.metadataUri"
                                            value={formik.values.metaplex.metadataUri}
                                            placeholder={t("Metadata URI")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.metaplex?.metadataUri && formik.errors.metaplex?.metadataUri)}
                                        />
                                    </div>
                                    {formik.touched.metaplex?.metadataUri && formik.errors.metaplex?.metadataUri && (
                                        <div className="text-danger fs-12">{formik.errors.metaplex.metadataUri}</div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Standard")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="standard"
                                            value={formik.values.standard}
                                            placeholder={t("Standard")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.standard && formik.errors.standard)}
                                        />
                                    </div>
                                    {formik.touched.standard && formik.errors.standard && (
                                        <div className="text-danger fs-12">{formik.errors.standard}</div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Symbol")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="symbol"
                                            value={formik.values.symbol}
                                            placeholder={t("Symbol")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.symbol && formik.errors.symbol)}
                                        />
                                    </div>
                                    {formik.touched.symbol && formik.errors.symbol && (
                                        <div className="text-danger fs-12">{formik.errors.symbol}</div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Decimals")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="number"
                                            name="decimals"
                                            value={formik.values.decimals}
                                            placeholder={t("Decimals")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.decimals && formik.errors.decimals)}
                                        />
                                    </div>
                                    {formik.touched.decimals && formik.errors.decimals && (
                                        <div className="text-danger fs-12">{formik.errors.decimals}</div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Fully Diluted Value")}</Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="number"
                                            name="fullyDilutedValue"
                                            value={formik.values.fullyDilutedValue}
                                            placeholder={t("Fully Diluted Value")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.fullyDilutedValue && formik.errors.fullyDilutedValue)}
                                        />
                                    </div>
                                    {formik.touched.fullyDilutedValue && formik.errors.fullyDilutedValue && (
                                        <div className="text-danger fs-12">{formik.errors.fullyDilutedValue}</div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Total Supply")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="totalSupply"
                                            value={formik.values.totalSupply}
                                            placeholder={t("Total Supply")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.totalSupply && formik.errors.totalSupply)}
                                        />
                                    </div>
                                    {formik.touched.totalSupply && formik.errors.totalSupply && (
                                        <div className="text-danger fs-12">{formik.errors.totalSupply}</div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Total Supply Formatted")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="totalSupplyFormatted"
                                            value={formik.values.totalSupplyFormatted}
                                            placeholder={t("Total Supply Formatted")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.totalSupplyFormatted && formik.errors.totalSupplyFormatted)}
                                        />
                                    </div>
                                    {formik.touched.totalSupplyFormatted && formik.errors.totalSupplyFormatted && (
                                        <div className="text-danger fs-12">{formik.errors.totalSupplyFormatted}</div>
                                    )}
                                </div>
                            </Col>


                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Seller Fee Basis Points")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="metaplex.sellerFeeBasisPoints"
                                            value={formik.values.metaplex.sellerFeeBasisPoints}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.metaplex?.sellerFeeBasisPoints && formik.errors.metaplex?.sellerFeeBasisPoints)}
                                        />
                                    </div>
                                    {formik.touched.metaplex?.sellerFeeBasisPoints && formik.errors.metaplex?.sellerFeeBasisPoints && (
                                        <div className="text-danger fs-12">{formik.errors.metaplex.sellerFeeBasisPoints}</div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Primary Sale Happened")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="metaplex.primarySaleHappened"
                                            value={formik.values.metaplex.primarySaleHappened}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.metaplex?.primarySaleHappened && formik.errors.metaplex?.primarySaleHappened)}
                                        />
                                    </div>
                                    {formik.touched.metaplex?.primarySaleHappened && formik.errors.metaplex?.primarySaleHappened && (
                                        <div className="text-danger fs-12">{formik.errors.metaplex.primarySaleHappened}</div>
                                    )}
                                </div>
                            </Col>

                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Master Edition")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="checkbox"
                                            name="metaplex.masterEdition"
                                            checked={formik.values.metaplex.masterEdition}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.metaplex?.masterEdition && formik.errors.metaplex?.masterEdition)}
                                        />
                                    </div>
                                    {formik.touched.metaplex?.masterEdition && formik.errors.metaplex?.masterEdition && (
                                        <div className="text-danger fs-12">{formik.errors.metaplex.masterEdition}</div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={12} md={6}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Is Mutable")} <span className="text-danger fs-11">*</span></Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="checkbox"
                                            name="metaplex.isMutable"
                                            checked={formik.values.metaplex.isMutable}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.metaplex?.isMutable && formik.errors.metaplex?.isMutable)}
                                        />
                                    </div>
                                    {formik.touched.metaplex?.isMutable && formik.errors.metaplex?.isMutable && (
                                        <div className="text-danger fs-12">{formik.errors.metaplex.isMutable}</div>
                                    )}
                                </div>
                            </Col>

                            <Col sm={12} md={12}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Links")}</Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="text"
                                            name="links.moralis"
                                            value={formik.values.links.moralis}
                                            placeholder={t("Moralis Link")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            invalid={!!(formik.touched.links?.moralis && formik.errors.links?.moralis)}
                                        />
                                    </div>
                                    {formik.touched.links?.moralis && formik.errors.links?.moralis && (
                                        <div className="text-danger fs-12">{formik.errors.links.moralis}</div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={12}>
                                <div className="mb-3">
                                    <Label className="text-primary fs-13">{t("Description")}</Label>
                                    <div>
                                        <Input
                                            className="no-icon-invalid"
                                            type="textarea"
                                            name="description"
                                            value={formik.values.description}
                                            placeholder={t("Description")}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            rows={4}
                                        />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        {/* Buttons */}
                        <Row>
                            <Col sm={12} className="mb-2 mt-3">
                                <div className="d-flex gap-3 justify-content-end align-items-center">
                                    <button
                                        className="btn btn-secondary fs-14 px-1 d-flex justify-content-center align-items-center bg-[#878a99] hover:bg-[#737582]"
                                        type="button"
                                        onClick={onCloseClick}
                                        disabled={isLoading}
                                        style={{ width: "105px" }}
                                    >
                                        <span className="ms-1">{t("Button Close")}</span>
                                    </button>
                                    <button
                                        className="btn btn-primary fs-14 px-1 d-flex justify-content-center align-items-center bg-[#009ef7] hover:bg-[#0086d2]"
                                        type="submit"
                                        disabled={isLoading}
                                        style={{ width: "165px" }}
                                    >
                                        {isLoading && <Spinner size="sm" className="me-2" />}
                                        <span>{t("Add Token")}</span>
                                    </button>
                                </div>
                            </Col>
                        </Row>
                    </form>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default TokenForm;